import { APIScope } from "../API/APIScope.Interfaces";
import { CoachingNudgeInterface, CoachingScoreSnapshotInterface } from "../Coaching/Coaching.Interface";
import { EscalationInterface } from "../Escalations/Escalations.Interface";
import { AvailableMembersMap } from "../Members/Members.Interface";
import { NotificationBaseInterface, NotificationInterface } from "../Notifications/Notifications.Interface";
import { SessionInterface } from "../Sessions/Sessions.Interface";
import { TicketPreview } from "../Tickets/Ticketing.Interfaces";
import { OnlineRosterEntry } from "../User/User.Interface";

export enum SocketEventType {
  AVAILABLE_MEMBERS = "available_members",
  SESSION = "session",
  ESCALATION = "escalation",
  NOTIFICATION = "notification",
  VARS = "vars",
  USERS_ONLINE = "users_online",
  USER_UPDATED = "user_updated",
  TICKET = "ticket",

  MESSAGE = "msg",

  // Contact Center V3 events:
  CC_CONVERSATION_UPDATE = "cc:conversation_update",
  CC_ASSIGNMENT = "cc:assignment",
  CC_AGENT_MESSAGE = "cc:agent_message",
  CC_PRESENCE = "cc:presence",
  CC_VOICE_EVENT = "cc:voice_event",

  // Coaching (ENG-2957) — emitted to the conversation room AND the org
  // live-agents room (joined only while the live-agents page is open):
  COACHING_SCORE = "coaching:score",
  COACHING_NUDGE = "coaching:nudge",
  COACHING_SUMMARY = "coaching:summary",
  /** The BOT set the conversation's focus (load_location/load_unit, widget tap) — the Selected box refreshes live (ENG-3051). */
  COACHING_FOCUS = "coaching:focus",
  /** The coaching engine judged call-script steps done (ENG-3402) — the Script section ticks live. */
  COACHING_SCRIPT = "coaching:script",
  /** Room-only key for the org-level live-agents subscription (no event of its own). */
  COACHING_LIVE_AGENTS = "coaching:live_agents",

  // Pulse (ENG-3432) — sent to the member room when a new run is saved:
  PULSE_REFRESH = "pulse:refresh",

  // Metadata events:
  META_ROOM_STATE_REQUEST = "meta_room_state_request"
}

export type SubscribableSocketEventType =
  | SocketEventType.AVAILABLE_MEMBERS
  | SocketEventType.SESSION
  | SocketEventType.ESCALATION
  | SocketEventType.NOTIFICATION
  | SocketEventType.VARS
  | SocketEventType.USERS_ONLINE
  | SocketEventType.CC_CONVERSATION_UPDATE
  | SocketEventType.CC_ASSIGNMENT
  | SocketEventType.CC_PRESENCE
  | SocketEventType.CC_VOICE_EVENT
  | SocketEventType.COACHING_LIVE_AGENTS;

export interface RoomStateRequestData {
  subscriptions: {
    [SocketEventType.VARS]?: { userId: number }; // Note this uses userId instead of orgId
    [SocketEventType.NOTIFICATION]?: { memberId: number }; // Note this uses memberId instead of orgId
    [SocketEventType.AVAILABLE_MEMBERS]?: { orgId: number }; // Note this uses memberId instead of orgId
    [SocketEventType.SESSION]?: { orgId: number };
    [SocketEventType.USERS_ONLINE]?: { orgId: number };
    [SocketEventType.ESCALATION]?: { orgId: number };
    [SocketEventType.CC_CONVERSATION_UPDATE]?: { orgId: number };
    [SocketEventType.CC_ASSIGNMENT]?: { memberId: number };
    [SocketEventType.CC_PRESENCE]?: { orgId: number };
    [SocketEventType.CC_VOICE_EVENT]?: { orgId: number };
    [SocketEventType.COACHING_LIVE_AGENTS]?: { orgId: number };
  };
  jwt: string;
  orgId: number;
}

export const SubscriptionKey = {
  forAvailableMembers: (orgId: number) => `${SocketEventType.AVAILABLE_MEMBERS}:${orgId}`,
  forSessions: (orgId: number) => `${SocketEventType.SESSION}:${orgId}`,
  forEscalations: (orgId: number) => `${SocketEventType.ESCALATION}:${orgId}`,
  // Uses userId/memberId instead of orgId:
  forVars: (memberId: number) => `${SocketEventType.VARS}:${memberId}`,
  forNotifications: (userId: number) => `${SocketEventType.NOTIFICATION}:${userId}`,
  forUserUpdated: (orgId: number) => `${SocketEventType.USER_UPDATED}:${orgId}`,
  forUsersOnline: (orgId: number) => `${SocketEventType.USERS_ONLINE}:${orgId}`,
  // Contact Center V3:
  forCCConversations: (orgId: number) => `${SocketEventType.CC_CONVERSATION_UPDATE}:${orgId}`,
  forCCAssignment: (memberId: number) => `${SocketEventType.CC_ASSIGNMENT}:${memberId}`,
  forCCPresence: (orgId: number) => `${SocketEventType.CC_PRESENCE}:${orgId}`,
  forCCVoiceEvent: (orgId: number) => `${SocketEventType.CC_VOICE_EVENT}:${orgId}`,
  // Coaching (ENG-2957) — org-level room for the live-agents page:
  forCoachingLiveAgents: (orgId: number) => `${SocketEventType.COACHING_LIVE_AGENTS}:${orgId}`,
  // Only used by calls to Conversations.Manager.ts:
  // forConversationItems: (orgId: number) => `${SocketEventType.CONVERSATION_ITEMS}:${orgId}`,
  // forConversationActivity: (orgId: number) => `${SocketEventType.CONVERSATION_ACTIVITY}:${orgId}`,
  // Only used by WebSocket.Channel.ts / MessageRouter.ts:
  forMessage: (userId: number) => `${SocketEventType.MESSAGE}:${userId}` // Note use of userId
};

export const SocketEventRequiredAPIScope = {
  [SocketEventType.AVAILABLE_MEMBERS]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.SESSION]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.ESCALATION]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.VARS]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.NOTIFICATION]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.USERS_ONLINE]: APIScope.CONVERSATION_VIEW,

  // Contact Center V3:
  [SocketEventType.CC_CONVERSATION_UPDATE]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.CC_ASSIGNMENT]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.CC_AGENT_MESSAGE]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.CC_PRESENCE]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.CC_VOICE_EVENT]: APIScope.CONVERSATION_VIEW,

  // Coaching (ENG-2957):
  [SocketEventType.COACHING_SCORE]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.COACHING_NUDGE]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.COACHING_SUMMARY]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.COACHING_FOCUS]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.COACHING_SCRIPT]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.COACHING_LIVE_AGENTS]: APIScope.CONVERSATION_VIEW,

  // [SocketEventType.CONVERSATION_ITEMS]: APIScope.CONVERSATION_VIEW,
  // [SocketEventType.CONVERSATION_ACTIVITY]: APIScope.CONVERSATION_VIEW,
  [SocketEventType.MESSAGE]: APIScope.CONVERSATION_VIEW
};

export interface SocketEventAvailableMembersUpdated {
  type: SocketEventType.AVAILABLE_MEMBERS;
  orgId: number;
  payload: AvailableMembersMap;
}
export interface SocketEventUpdateSession {
  type: SocketEventType.SESSION;
  orgId: number;
  payload: SessionInterface;
}
export interface SocketEventUpdateEscalation {
  type: SocketEventType.ESCALATION;
  orgId: number;
  payload: EscalationInterface;
}

export interface SocketEventUpdateUsersOnline {
  type: SocketEventType.USERS_ONLINE;
  orgId: number;
  payload: { [userId: number]: OnlineRosterEntry };
}
export interface SocketEventUpdateVars {
  type: SocketEventType.VARS;
  userId: number; // Note userId instead of orgId
  payload: { [key: string]: string | undefined }; // AdamTodo any?
}

export interface SocketEventUserUpdated {
  type: SocketEventType.USER_UPDATED;
  orgId: number;
  userId: number;
  payload: { [key: string]: string | undefined }; // AdamTodo any?
}
export interface SocketEventNotification {
  type: SocketEventType.NOTIFICATION;
  memberId: number; // Note MEMBER id
  payload: NotificationInterface; // AdamTodo is this EscalationNotificationInterface[]?
}

/**
 * Ticket create/update events ride the BOT socket's org room (ENG-3005,
 * 2026-08-08) via the cc-broadcast relay, which wraps the preview as
 * `{ payload }`. The old notifications-server transport (typed event with
 * orgId, room subscriptions, `SubscriptionKey.forTickets`) is deleted.
 */
export interface SocketEventTicketUpdated {
  payload: TicketPreview;
}

// ─── Contact Center V3 Events ───────────────────────────────────────────────

export interface SocketEventCCConversationUpdate {
  type: SocketEventType.CC_CONVERSATION_UPDATE;
  orgId: number;
  payload: {
    action: "new_message" | "escalation_started" | "escalation_ended" | "status_changed";
    botConversationId: number;
    userId: number;
    channel: string;
    /** Partial data — enough for the conversation list to update without a full refetch */
    lastMessageContent?: string;
    lastMessageAt?: string;
    /** Latest messageIndex in the conversation — used for incremental fetching */
    latestMessageIndex?: number;
    /** Role of the new message ("user" | "assistant" | "agent") — in-place list patching increments unread only for user messages (ENG-2827). */
    role?: string;
    /** Sender when role is "agent" — lets a CC tab skip the message ding for its own sends (ENG-2946). */
    agentMemberId?: number;
    escalationStatus?: string;
    agentMemberName?: string;
    /** Channel this online status applies to (for status_changed events) */
    userOnlineChannel?: string;
    /** Whether the user is online on that channel (for status_changed events) */
    userOnline?: boolean;
    /**
     * Whether the user is actively viewing the chat (panel open + tab focused),
     * vs connected-but-chat-closed. Lets the CC show three states:
     * viewing (online), present (chat closed), offline. Only meaningful when
     * `userOnline` is true.
     */
    userViewing?: boolean;
  };
}

export interface SocketEventCCAssignment {
  type: SocketEventType.CC_ASSIGNMENT;
  memberId: number;
  payload: {
    /** `withdrawn`: the offer was retracted before the agent acted — e.g. the
     *  voice caller hung up mid-ring (ENG-2929). Distinct from `timed_out`
     *  (the agent let the countdown lapse) and `completed` (lifecycle end). */
    action: "offered" | "accepted" | "rejected" | "completed" | "transferred" | "timed_out" | "withdrawn";
    assignmentId: number;
    botConversationId: number;
    channel: string;
    userId: number;
    userName: string;
    escalationReason?: string;
    /** Server-authoritative offer deadline (ISO) — the toast countdown renders from this, never a client-local timer (ENG-2826). */
    expiresAt?: string;
  };
}

export interface SocketEventCCAgentMessage {
  type: SocketEventType.CC_AGENT_MESSAGE;
  orgId: number;
  payload: {
    botConversationId: number;
    messageId: number;
    content: string;
    channel: string;
    agentMemberId: number;
    agentName: string;
    timestamp: string;
  };
}

export interface SocketEventCCPresence {
  type: SocketEventType.CC_PRESENCE;
  orgId: number;
  payload: {
    memberId: number;
    status: "online" | "busy" | "idle" | "offline";
    activeConversationCount?: number;
    /**
     * The member who CAUSED this transition, when it wasn't the agent
     * themselves (ENG-3052) — a manager setting someone offline from the Agent
     * Dashboard. Absent for self-service and heartbeat-driven transitions.
     *
     * The affected agent's client uses this to decide whether to raise the
     * "Your manager set your status to Offline." toast: without it, a manager
     * action is indistinguishable from an ordinary presence tick.
     */
    setByMemberId?: number;
  };
}

// ─── Coaching Events (ENG-2957) ─────────────────────────────────────────────
// Emitted per tick to the conversation room (whoever has the conversation
// open) and the org live-agents room (live-agents page only). Payloads carry
// the persisted rows, so a reload can always rebuild state from the DB.

export interface SocketEventCoachingScore {
  type: SocketEventType.COACHING_SCORE;
  orgId: number;
  payload: {
    botConversationId: number;
    snapshot: CoachingScoreSnapshotInterface;
  };
}

export interface SocketEventCoachingNudge {
  type: SocketEventType.COACHING_NUDGE;
  orgId: number;
  payload: {
    botConversationId: number;
    /** Fired, or lifecycle-updated (pinned/dismissed/finished), nudge row. */
    nudge: CoachingNudgeInterface;
  };
}

export interface SocketEventCoachingFocus {
  type: SocketEventType.COACHING_FOCUS;
  orgId: number;
  payload: {
    botConversationId: number;
    /** Ids only — the client refetches for display names. */
    focus: { locationId?: number | null; unitId?: number | null };
  };
}

export interface SocketEventCoachingSummary {
  type: SocketEventType.COACHING_SUMMARY;
  orgId: number;
  payload: {
    botConversationId: number;
    runningSummary: string;
    /** Tick the summary was generated at (pairs it with a score snapshot). */
    asOfSeq: number;
  };
}

export interface SocketEventCCVoiceEvent {
  type: SocketEventType.CC_VOICE_EVENT;
  orgId: number;
  payload: {
    action: "call_started" | "call_ended" | "agent_joined" | "agent_left" | "listen_started" | "barge_in" | "recording_ready" | "agent_leg_failed";
    /** agent_leg_failed (ENG-3408): the Twilio status of the leg that never joined (busy / no-answer / failed / canceled / bridge_timeout). */
    legStatus?: string;
    callSid: string;
    botConversationId?: number;
    userId?: number;
    memberId?: number;
    /** Duration in seconds (for call_ended) */
    duration?: number;
    /** Recording URL (for recording_ready) */
    recordingUrl?: string;
  };
}
